import { useId, useState } from 'react';
import { campaignCopy, isRollbackReasonFor, rollbackReasonsFor } from '@discovr/features';
import type { RollbackKind, RollbackReasonCode } from '@discovr/types';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TypedConfirmField, isTypedConfirmMatch } from '../../components/common/TypedConfirmField';
import { useLastNonEmpty } from '../hooks/useLastNonEmpty';
import { holdNoteLengthError } from './ConfirmedTab.holdReasons';
import { HoldNoteField } from './HoldNoteField';

const copy = campaignCopy.creator.rollback;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatorName: string;
  kind: RollbackKind | null;
  busy: boolean;
  onCloseAutoFocus?: (event: Event) => void;
  onConfirm: (reasonCode: RollbackReasonCode, note: string) => void | Promise<void>;
}

export function RollbackApprovalDialog({
  open,
  onOpenChange,
  creatorName,
  kind,
  busy,
  onConfirm,
  onCloseAutoFocus,
}: Props) {
  const [reason, setReason] = useState<RollbackReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const reasonId = useId();
  const reasonHintId = useId();
  const shownName = useLastNonEmpty(creatorName);
  const shownKind = useLastNonEmpty(kind ?? '') as RollbackKind | '';
  const content = shownKind ? copy.kinds[shownKind] : null;
  const reasons = shownKind ? rollbackReasonsFor(shownKind) : [];

  const noteError = holdNoteLengthError(note);
  const canSubmit = kind !== null && reason !== '' && noteError === null && isTypedConfirmMatch(typedConfirm) && !busy;

  const showReasonHint = reasonTouched && reason === '';

  const reset = () => {
    setReason('');
    setNote('');
    setTypedConfirm('');
    setReasonTouched(false);
  };
  if (!open && (reason !== '' || note !== '' || typedConfirm !== '' || reasonTouched)) reset();

  const submit = () => {
    if (canSubmit && reason) void onConfirm(reason, note.trim());
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && busy) return;
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{content ? copy.dialog.title(shownName, content.from, content.to) : ''}</DialogTitle>
          <DialogDescription>{content?.body}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 text-sm">
            <p>
              <span className="font-medium">{copy.dialog.changesLabel}</span> <span>{content?.changes}</span>
            </p>
            <p>
              <span className="font-medium">{copy.dialog.staysLabel}</span> <span>{content?.stays}</span>
            </p>
            <p className="text-muted-foreground">{copy.dialog.brandNote}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-1 text-sm font-medium">
              <label htmlFor={reasonId}>{copy.dialog.reasonLabel}</label>
              <span aria-hidden data-testid="reason-required-marker" className="text-destructive">
                *
              </span>
            </div>
            <Select
              value={reason}
              onValueChange={(v) => {
                if (shownKind && isRollbackReasonFor(shownKind, v)) setReason(v);
              }}
              onOpenChange={(isOpen) => !isOpen && setReasonTouched(true)}
              disabled={busy}
            >
              <SelectTrigger
                id={reasonId}
                aria-required
                aria-invalid={showReasonHint || undefined}
                aria-describedby={showReasonHint ? reasonHintId : undefined}
              >
                <SelectValue placeholder={copy.dialog.reasonPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {showReasonHint && (
              <p id={reasonHintId} className="text-xs text-destructive">
                {copy.dialog.reasonRequired}
              </p>
            )}
          </div>
          <HoldNoteField
            label={copy.dialog.noteLabel}
            placeholder={null}
            value={note}
            onChange={setNote}
            error={noteError}
            disabled={busy}
          />
          <TypedConfirmField
            value={typedConfirm}
            onChange={setTypedConfirm}
            disabled={busy}
            onEnter={submit}
            labelAfter={copy.dialog.confirmLabelAfter}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={busy}>
            {copy.dialog.cancel}
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {copy.dialog.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
